import React, {
  useEffect, useId, useRef, useState,
} from 'react'

import { Button, Tooltip, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  string, func, bool,
} from 'prop-types'

const CARD_SELECTOR = '.card-3d-model'

function ModelCardActionButton({
  className = '',
  icon,
  id,
  name,
  onClick = undefined,
  tooltipContent,
  isLoading = false,
  ...rest
}) {
  const placeholderRef = useRef(null)
  const cardRef = useRef(null)
  const shouldRestoreFocusRef = useRef(false)
  const [isTooltipMounted, setIsTooltipMounted] = useState(false)
  // Triangle's Button takes no ref and renders an <a> when given href, so this instance's control is
  // found again through its own attribute rather than by tag or label.
  const controlKey = useId()

  // Every mounted react-tooltip listens for clicks on window and re-renders on each one, and a
  // results grid carries several per card (hundreds of re-renders per click anywhere on the page).
  // The tooltip only ever opens on mouse hover, so it mounts the first time a mouse enters the card,
  // before the pointer can reach the button. Touch never mounts it: swapping the button mid-tap
  // would drop the click.
  useEffect(() => {
    if (isTooltipMounted) {
      return undefined
    }

    const card = placeholderRef.current?.closest(CARD_SELECTOR)
    if (!card) {
      setIsTooltipMounted(true)
      return undefined
    }
    cardRef.current = card

    const handlePointerEnter = (event) => {
      if (event.pointerType !== 'mouse') {
        return
      }
      shouldRestoreFocusRef.current = Boolean(placeholderRef.current?.contains(document.activeElement))
      setIsTooltipMounted(true)
    }

    card.addEventListener('pointerenter', handlePointerEnter)
    return () => card.removeEventListener('pointerenter', handlePointerEnter)
  }, [isTooltipMounted])

  // Mounting the tooltip replaces the control element, so keyboard focus is handed to the new one.
  useEffect(() => {
    if (!isTooltipMounted || !shouldRestoreFocusRef.current) {
      return
    }
    shouldRestoreFocusRef.current = false
    const controls = cardRef.current?.querySelectorAll('[data-action-control]') || []
    Array.from(controls).find(control => control.dataset.actionControl === controlKey)?.focus()
  }, [isTooltipMounted])

  // Link actions (Purchased) pass href and no onClick.
  const handleClick = onClick ? () => onClick(id) : undefined

  // The tooltip is react-tooltip (data-for), not aria-labelledby/describedby, and the button
  // renders an empty icon span - so without aria-label these controls are announced as an
  // unlabelled "button". The tooltip copy is the same text a sighted user gets.
  const button = (
    <Button
      icon={<span className={classnames('button-icon', icon)} />}
      variant="only-icon"
      disabled={isLoading}
      // Triangle's Button maps actionType to the native type and defaults it to '', which the
      // HTML spec treats as submit. None of these are submit controls.
      actionType="button"
      aria-label={tooltipContent}
      data-action-control={controlKey}
      {...rest}
    />
  )

  if (!isTooltipMounted) {
    // Same two spans Triangle's Tooltip wraps the trigger in, so the swap does not move anything.
    return (
      <span ref={placeholderRef} className={className}>
        <span className="" onClick={handleClick} role="presentation">
          {button}
        </span>
      </span>
    )
  }

  return (
    <Tooltip
      content={tooltipContent}
      id={`${name}-${id}`}
      randomizeID
      onClick={handleClick}
      containerClass={className}
      offset={{ top: 10 }}
      disableMobileEvent
    >
      {button}
    </Tooltip>
  )
}

ModelCardActionButton.propTypes = {
  className: string,
  icon: string.isRequired,
  id: string.isRequired,
  name: string.isRequired,
  onClick: func,
  tooltipContent: string.isRequired,
  isLoading: bool,
}

export default ModelCardActionButton
